import React, { useEffect, useMemo } from 'react';
import { configureChains, mainnet, createConfig, WagmiConfig } from 'wagmi';
import { publicProvider } from 'wagmi/providers/public';
import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { Connector } from './Connector.js';
import { SyncDynamicWagmi } from './SyncDynamicWagmi.js';
import { getWagmiChainsFromDynamicChains } from './getWagmiChainsFromDynamicChains.js';
import { getWagmiProvidersFromDynamicChains } from './getWagmiProvidersFromDynamicChains.js';
import { getConnector } from './getConnector.js';

/**
 * we create a "default" (static chain and provider) client to use in the WagmiConfig
 * until we've loaded the dynamic chains and providers, at which point we update the client
 */
let connector = undefined;
const { publicClient } = configureChains([mainnet], [publicProvider()]);
const config = createConfig({
    autoConnect: true,
    publicClient,
});
const DynamicWagmiConnector = ({ evmNetworks: customerSuppliedEvmNetworks, children, }) => {
    const { networkConfigurations, handleLogOut, connectedWallets, linkedWallets, authMode, sendWagmiSettings, } = useDynamicContext();
    const wallets = authMode === 'connect-only' ? connectedWallets : linkedWallets;
    useEffect(() => {
        // only send wagmi settings once, when the first time DynamicWagmiConnector renders
        sendWagmiSettings({
            dynamicWagmiSettings: customerSuppliedEvmNetworks,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const chains = useMemo(() => {
        var _a;
        const evmNetworks = ((_a = customerSuppliedEvmNetworks !== null && customerSuppliedEvmNetworks !== void 0 ? customerSuppliedEvmNetworks : networkConfigurations === null || networkConfigurations === void 0 ? void 0 : networkConfigurations.evm) !== null && _a !== void 0 ? _a : []).map((network) => {
            network.chainId = parseInt(network.chainId.toString());
            return network;
        });
        const mappedChains = getWagmiChainsFromDynamicChains(evmNetworks);
        const mappedProviders = getWagmiProvidersFromDynamicChains(evmNetworks);
        const { chains, publicClient } = configureChains(mappedChains, mappedProviders);
        config.setPublicClient(publicClient);
        return chains;
    }, [customerSuppliedEvmNetworks, networkConfigurations === null || networkConfigurations === void 0 ? void 0 : networkConfigurations.evm]);
    connector = getConnector(wallets, (wallet) => new Connector({
        chains,
        handleLogOut,
        walletConnector: wallet.connector,
    }));
    config.setConnectors(connector ? [connector] : []);
    // use React.createElement to prevent bunding react/jsx-runtime,
    // which is not compatible when bundling apps using React 17
    return React.createElement(WagmiConfig, { config: config }, React.createElement(SyncDynamicWagmi, { connector }, children));
};

export { DynamicWagmiConnector };
